// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
// Global modules
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, CreateAxiosDefaults } from 'axios';
import omit from 'lodash.omit';

// Modules
import { setIsAuthorized } from '@/modules/Auth';

// Store
import { store } from '@/store';

// Utils/Helpers
import { API_URL, ResponseStatusCodes } from '@/constants/common';
import { COOKIE_KEYS } from '@/constants/cookies';
import appCookiesStorage from './appCookies';

const defaultConfigs: AxiosRequestConfig = {
  baseURL: API_URL,
};

interface RefreshTokenResponse {
  refresh_token: string;
}

interface HttpClientRequestConfig extends AxiosRequestConfig {
  isRetry?: boolean;
}

interface HttpClientError extends AxiosError {
  config: HttpClientRequestConfig;
}

class HttpClient<TClient extends AxiosInstance> {
  private readonly client;
  private refreshRequest: Promise<AxiosResponse<RefreshTokenResponse>> | null;
  private refreshToken;

  get;
  post;
  put;
  patch;
  delete;

  constructor(client: TClient) {
    this.client = client;
    this.refreshRequest = null;
    this.refreshToken = appCookiesStorage.getItem(COOKIE_KEYS.REFRESH_TOKEN);

    if (this.refreshToken) {
      appCookiesStorage.removeItem(COOKIE_KEYS.REFRESH_TOKEN);
    }

    const requestInterceptor = (config: AxiosRequestConfig) => {
      const isUrlSearchParams = config.params instanceof URLSearchParams;

      const authToken =
        appCookiesStorage.getItem(COOKIE_KEYS.AUTH_TOKEN) ||
        (isUrlSearchParams
          ? config.params.get(COOKIE_KEYS.AUTH_TOKEN)
          : (config?.params?.[COOKIE_KEYS.AUTH_TOKEN] as string)) ||
        '';

      if (isUrlSearchParams) {
        config.params.delete(COOKIE_KEYS.AUTH_TOKEN);
      }

      const updated = {
        headers: {},
        ...config,
        params: isUrlSearchParams ? config.params : omit(config.params || {}, [COOKIE_KEYS.AUTH_TOKEN]),
      };

      if (authToken) {
        updated.headers.Authorization = `Bearer ${authToken}`;
      }

      return updated;
    };

    const errorResponseInterceptor = async (error: HttpClientError) => {
      if (!error || !error.response) {
        throw new Error('Server error');
      }

      // Only a genuine authentication failure (401) ends the session and bounces the
      // admin to sign-in. A 403 means the admin IS authenticated but lacks permission
      // for this specific resource — it must propagate as a normal error so the calling
      // page / route guard can handle it (surface an error, redirect to /profile, …) and
      // must NOT log the admin out. The block below lets the 403 fall through and throw.
      if (error.response.status === ResponseStatusCodes.NOT_AUTHORIZED) {
        this.refreshRequest = null;

        store.dispatch(setIsAuthorized(false));
        throw error as Error;
      }

      if (!this.refreshToken || error.response.status !== ResponseStatusCodes.SESSION_ENDED || error.config.isRetry) {
        this.refreshRequest = null;
        throw error as Error;
      }

      if (!this.refreshRequest) {
        this.refreshRequest = this.client.get('/refresh', { params: { refresh_token: this.refreshToken } });
      }

      appCookiesStorage.removeItem(COOKIE_KEYS.REFRESH_TOKEN);

      const { data } = await this.refreshRequest;

      this.refreshToken = data?.refresh_token;
      appCookiesStorage.setItem(COOKIE_KEYS.REFRESH_TOKEN, this.refreshToken);

      const newRequest = {
        ...error.config,
        isRetry: true,
      };

      return this.client(newRequest);
    };

    /** Adding the request interceptors */
    this.client.interceptors.request.use(requestInterceptor);

    /** Adding the response interceptors */
    this.client.interceptors.response.use((r: AxiosResponse) => r, errorResponseInterceptor);

    this.get = this.client.get.bind(this);
    this.post = this.client.post.bind(this);
    this.put = this.client.put.bind(this);
    this.patch = this.client.patch.bind(this);
    this.delete = this.client.delete.bind(this);
  }
}

const defaultClient = axios.create(defaultConfigs as CreateAxiosDefaults);

export const isHttpClientError = axios.isAxiosError;

export default new HttpClient(defaultClient);

export type { AxiosError as HttpClientError };
export type ApiResponse<Response> = Promise<AxiosResponse<Response>>;
